import { h, Fragment } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { useStateValue } from '../../state-management/store'
import { fetchProcedureProviders } from '../../state-management/actions'
import Row from '../../components/grid/row'
import Col from '../../components/grid/col'
import { CustomField, CustomFieldType } from '../custom-field'
import { Sex, PatientType } from '../../models/enums'
import { ValueType, PatientDetails } from '../../models/patient'
import { Spinner } from '../spinner'
import { validator } from '../../shared/validator'
import { softGreen } from '../../shared/colors'
import * as style from './style.css'
import globalVars from '../../shared/global'
import { useAsync } from '../../shared/custom-hooks/useAsync'
import CustomPhoneInput from '../custom-field/custom-phone-input'

const PatientForm: preact.FunctionalComponent = () => {
    const [
        {
            selectedLocationId,
            patientType,
            patientDetails,
            procedures,
            schedulingProcedures,
            providers,
            submitting,
            customization,
        },
        dispatch,
    ] = useStateValue()
    const [focusedField, setFocusedField] = useState<string | undefined>(undefined)

    const filteredProcedures =
        schedulingProcedures && schedulingProcedures.length > 0
            ? procedures.filter((procedure) => schedulingProcedures.includes(procedure.id))
            : procedures

    const acceptableProcedureTypes = [PatientType.BOTH, patientType]

    const trim = (value?: string) => (value ? value.trim() : undefined)

    const { run: runFetchProcedureProviders, status: fetchProcedureProvidersStatus } = useAsync({ status: 'pending' })

    const enabledProviderSelection =
        customization?.scheduling_additional_features_and_fields.enabled_provider_selection ?? false

    const isInsuranceQuestionShown = ['optional', 'required'].includes(
        customization?.scheduling_additional_features_and_fields?.insurance ?? ''
    )

    const handleChange = async (fieldName: keyof PatientDetails, value?: ValueType) => {
        const error = validator(patientDetails[fieldName], value)
        await dispatch({
            type: 'UPDATE_PATIENT_DETAILS',
            fieldName,
            value,
            error,
        })
    }

    useEffect(() => {
        const isSelectedProviderValid =
            !patientDetails.provider.value ||
            providers.some((provider) => provider.id === patientDetails.provider.value)
        if (!isSelectedProviderValid && fetchProcedureProvidersStatus === 'resolved') {
            handleChange('provider', '')
        }
    }, [providers, patientDetails.provider.value, fetchProcedureProvidersStatus])

    useEffect(() => {
        if (patientDetails.reasonOfAppointment.value && enabledProviderSelection) {
            runFetchProcedureProviders(
                fetchProcedureProviders(dispatch, selectedLocationId, patientDetails.reasonOfAppointment.value)
            )
        } else {
            handleChange('provider', '')
        }
    }, [
        patientDetails.reasonOfAppointment.value,
        runFetchProcedureProviders,
        dispatch,
        selectedLocationId,
        enabledProviderSelection,
    ])

    const handleProcedureChange = async (value: string) => {
        await handleChange('reasonOfAppointment', value)

        dispatch({
            type: 'RESET_APPOINTMENT_DATETIME',
        })
    }

    const handleFocusField = (field: string) => () => setFocusedField(field)

    if (submitting) {
        return (
            <div className={style.spinnerContainer}>
                <Spinner color={softGreen} />
            </div>
        )
    }

    return (
        <div class={style.patientForm}>
            <h2>Please provide the following information</h2>
            <Row>
                <Col xs={6}>
                    <CustomField
                        label="First Name*"
                        isDirty={patientDetails.firstName.isDirty}
                        value={patientDetails.firstName.value}
                        required={patientDetails.firstName.isRequired}
                        error={patientDetails.firstName.error}
                        inputProps={{ maxLength: 15 }}
                        onFocus={handleFocusField('firstName')}
                        onChange={(value?: ValueType) => handleChange('firstName', value as string)}
                    />
                </Col>
                <Col xs={6}>
                    <CustomField
                        label="Last Name*"
                        isDirty={patientDetails.lastName.isDirty}
                        value={patientDetails.lastName.value}
                        required={patientDetails.lastName.isRequired}
                        error={patientDetails.lastName.error}
                        inputProps={{ maxLength: 20 }}
                        onFocus={handleFocusField('lastName')}
                        onChange={(value?: ValueType) => handleChange('lastName', value as string)}
                    />
                </Col>
                <Col sm={3} xs={6}>
                    <CustomField
                        label="Date of Birth*"
                        customFieldType={CustomFieldType.DATEPICKER}
                        value={patientDetails.dateOfBirth.value}
                        required={patientDetails.dateOfBirth.isRequired}
                        error={patientDetails.dateOfBirth.error}
                        placeholder="MM/DD/YYYY"
                        onFocus={handleFocusField('dateOfBirth')}
                        isFocused={focusedField === 'dateOfBirth'}
                        hidePicker={true}
                        onChange={(value?: ValueType) => handleChange('dateOfBirth', value as string)}
                    />
                </Col>
                <Col sm={3} xs={6}>
                    <CustomPhoneInput
                        label="Phone*"
                        value={patientDetails.phone.value}
                        error={patientDetails.phone.error}
                        rules={{ required: true }}
                        onChange={(value?: ValueType) => handleChange('phone', value as string)}
                        country={'US'}
                    />
                </Col>

                <Col sm={4} xs={7}>
                    <CustomField
                        label="Email Address*"
                        isDirty={patientDetails.emailAddress.isDirty}
                        inputType="email"
                        value={patientDetails.emailAddress.value}
                        required={patientDetails.emailAddress.isRequired}
                        error={patientDetails.emailAddress.error}
                        onFocus={handleFocusField('emailAddress')}
                        onChange={(value?: ValueType) => handleChange('emailAddress', trim(value as string))}
                    />
                </Col>
                {patientType === PatientType.NEW && (
                    <Fragment>
                        <Col sm={2} xs={5}>
                            <CustomField
                                label="Sex"
                                customFieldType={CustomFieldType.BUTTON_GROUP}
                                value={patientDetails.sex.value}
                                required={patientDetails.sex.isRequired}
                                error={patientDetails.sex.error}
                                options={[
                                    {
                                        key: Sex.MALE,
                                        value: Sex.MALE,
                                    },
                                    {
                                        key: Sex.FEMALE,
                                        value: Sex.FEMALE,
                                    },
                                ]}
                                onFocus={handleFocusField('sex')}
                                onChange={(value?: ValueType) => handleChange('sex', value as Sex)}
                            />
                        </Col>
                    </Fragment>
                )}
                <Col sm={6} xs={12}>
                    <CustomField
                        label="Reason for Appointment*"
                        customFieldType={CustomFieldType.SELECT}
                        value={patientDetails.reasonOfAppointment.value}
                        required={patientDetails.reasonOfAppointment.isRequired}
                        error={patientDetails.reasonOfAppointment.error}
                        disabled={filteredProcedures.length === 0}
                        onFocus={handleFocusField('reasonOfAppointment')}
                        onChange={(value?: ValueType) => handleProcedureChange(value as string)}
                    >
                        {globalVars.isRSVP ? (
                            filteredProcedures.length > 0 ? (
                                <Fragment>
                                    <option disabled selected hidden value="">
                                        Select Reason for Appointment
                                    </option>

                                    {filteredProcedures.map((procedure) => (
                                        <option key={procedure.id} value={procedure.id}>
                                            {procedure.name}
                                        </option>
                                    ))}
                                </Fragment>
                            ) : (
                                <option value={patientDetails.reasonOfAppointment.value} selected={true}>
                                    Campaigns
                                </option>
                            )
                        ) : (
                            filteredProcedures.length > 0 && (
                                <Fragment>
                                    <option disabled selected hidden value="">
                                        Select Reason for Appointment
                                    </option>

                                    {filteredProcedures
                                        .filter(
                                            (procedure) =>
                                                procedure.enabled === true &&
                                                acceptableProcedureTypes.includes(procedure.patient_type)
                                        )
                                        .map((procedure) => (
                                            <option key={procedure.id} value={procedure.id}>
                                                {procedure.name}
                                            </option>
                                        ))}
                                </Fragment>
                            )
                        )}
                    </CustomField>
                </Col>
                {enabledProviderSelection && (
                    <Col sm={6} xs={12}>
                        <CustomField
                            label="Provider"
                            customFieldType={CustomFieldType.SELECT}
                            value={patientDetails.provider.value}
                            required={patientDetails.provider.isRequired}
                            error={patientDetails.provider.error}
                            disabled={
                                !providers.length ||
                                !patientDetails.reasonOfAppointment.isValid ||
                                fetchProcedureProvidersStatus === 'pending'
                            }
                            onFocus={handleFocusField('provider')}
                            onChange={(value?: ValueType) => handleChange('provider', trim(value as string))}
                            placeholder="Any Provider"
                        >
                            <option selected={patientDetails.provider.value === ''} value="">
                                Any Provider
                            </option>
                            {providers.map((provider) => (
                                <option key={provider.id} value={provider.id}>
                                    {[provider.suffix, provider.first_name, provider.middle_initial, provider.last_name]
                                        .filter(Boolean)
                                        .join(' ')}
                                </option>
                            ))}
                        </CustomField>
                    </Col>
                )}
            </Row>
            {isInsuranceQuestionShown && (
                <Row>
                    <Col>
                        <CustomField
                            label={`Does the patient have insurance?${
                                patientDetails.hasInsurance.isRequired ? '*' : ''
                            }`}
                            customFieldType={CustomFieldType.RADIOBUTTON_GROUP}
                            wrapperClassName={style.radioClass}
                            value={patientDetails.hasInsurance.value}
                            required={patientDetails.hasInsurance.isRequired}
                            options={[
                                {
                                    key: 'yes',
                                    value: 'Yes',
                                },
                                {
                                    key: 'no',
                                    value: 'No',
                                },
                            ]}
                            onFocus={handleFocusField('hasInsurance')}
                            onChange={(value?: ValueType) => handleChange('hasInsurance', value)}
                        />
                    </Col>
                </Row>
            )}
        </div>
    )
}

export default PatientForm
